import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import './AboutNew.css';

const About = () => {
  const observerRef = useRef(null);

  useEffect(() => {
    // Intersection Observer for scroll animations
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.scroll-fade');
    elements.forEach((el) => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return (
    <div className="about-page-new">
      {/* Hero Section */}
      <section className="about-hero-new">
        <div className="hero-background-new">
          <img 
            src="https://images.unsplash.com/photo-1477332552946-cfb384aeaf1c" 
            alt="Morning purpose"
            className="hero-bg-image"
          />
          <div className="hero-overlay-new"></div>
        </div>
        <div className="container hero-content-new">
          <h1 className="hero-title scroll-fade">
            Pay the Tax Early,<br />
            or Pay It Later.
          </h1>
          <p className="hero-subtitle scroll-fade">
            At Sympozium, we believe health is the foundation for every other part of life — success, wealth, relationships, and peace of mind.
          </p>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="philosophy-section-new section">
        <div className="container-narrow">
          <h2 className="section-heading scroll-fade">The Philosophy</h2>
          
          <div className="philosophy-content-new">
            <p className="philosophy-text scroll-fade">
              The way I see it, this isn't really about weight loss. It's about improving your life — every part of it. Your health sets the tone for everything else — your finances, your relationships, your confidence, your ability to think clearly and act with intent.
            </p>
            
            <blockquote className="philosophy-quote scroll-fade">
              Arthur Jones said you can go days without food, minutes without air, and years without exercise. And that's exactly why people take it for granted — the window is too long. The damage creeps up slowly, until one day it's too late.
            </blockquote>
            
            <p className="philosophy-text scroll-fade">
              The truth is, no amount of wealth, comfort, or success can make up for poor health. You can earn millions, but you can't buy back your energy, your vitality, or your time.
            </p>
          </div>
        </div>
      </section>

      {/* Tax of Existence Section */}
      <section className="tax-section section-large">
        <div className="container-narrow">
          <h2 className="section-heading-center scroll-fade">The Tax of Existence</h2>
          
          <div className="tax-content">
            <div className="tax-quote-block scroll-fade">
              <p className="tax-highlight">"You will pay it, one way or another."</p>
            </div>
            
            <p className="tax-text scroll-fade">
              There are two ways to pay the tax of existence. The first one costs effort — discipline, time, discomfort. The second one costs everything — your energy, your independence, your peace, your future.
            </p>
            
            <div className="tax-quote-block scroll-fade">
              <p className="tax-highlight">"The first one costs effort. The second costs everything."</p>
            </div>
            
            <div className="tax-split scroll-fade">
              <div className="tax-split-item">
                <div className="tax-split-image">
                  <img 
                    src="https://images.unsplash.com/photo-1506126613408-eca07ce68773" 
                    alt="Active living"
                  />
                </div>
                <h3>Pay It Early</h3>
                <p>Effort, discipline, and intention. The investment that compounds.</p>
              </div>
              
              <div className="tax-split-item">
                <div className="tax-split-image grayscale-effect">
                  <img 
                    src="https://images.unsplash.com/photo-1591228127791-8e2eaef098d3" 
                    alt="Reflection"
                  />
                </div>
                <h3>Pay It Later</h3>
                <p>Regret, limitation, and decline. The cost you can't afford.</p>
              </div>
            </div>
            
            <div className="tax-quote-block scroll-fade">
              <p className="tax-highlight">"Nobody escapes it. The only question is when and how you pay it."</p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Stand For */}
      <section className="stand-for-section section">
        <div className="container-narrow">
          <h2 className="section-heading-center scroll-fade">What We Stand For</h2>
          <p className="stand-subtitle scroll-fade">At Sympozium, we help you pay it the best way.</p>
          
          <div className="stand-grid">
            <div className="stand-item scroll-fade">
              <h3>Discipline over Motivation</h3>
              <p>Motivation fades. Systems and structure last.</p>
            </div>
            
            <div className="stand-item scroll-fade">
              <h3>Structure over Chaos</h3>
              <p>Clear plans, consistent action, measurable progress.</p>
            </div>
            
            <div className="stand-item scroll-fade">
              <h3>Sustainability over Extremes</h3>
              <p>Build habits you can maintain for life, not just weeks.</p>
            </div>
            
            <div className="stand-item scroll-fade">
              <h3>Accountability over Excuses</h3>
              <p>We show up. We expect the same from you.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Closing Section */}
      <section className="closing-section section-large">
        <div className="closing-background">
          <img 
            src="https://images.unsplash.com/photo-1502224562085-639556652f33" 
            alt="Time and purpose"
            className="closing-bg-image"
          />
          <div className="closing-overlay"></div>
        </div>
        <div className="container closing-content">
          <blockquote className="closing-quote scroll-fade">
            "It's not about the gym, the diet, or the mirror.<br />
            It's about time.<br />
            And time is the one thing you can't afford to waste."
          </blockquote>
          
          <Link to="/contact" className="btn btn-primary btn-large scroll-fade">
            Start Your Transformation
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;